import type { BudgetRow } from 'app/services/budget'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { compact, snakeCase } from 'lodash'
import moment from 'moment'
import { FilterableColumn, FilterValueType } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useBudgetStore } from 'app/stores/StoreContext'

export type UseBudgetListReturn = {
  filteredRows: BudgetRow[]
  columns: ColumnDef<BudgetRow>[]
  onAddNew: () => void
}

const filterableColumns: FilterableColumn[] = [
  {
    attribute: `yearMonth`,
    displayName: `Date`,
    type: FilterValueType.rangeDate,
    defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
  },
  { attribute: `amount`, displayName: `Budget`, type: FilterValueType.number },
  { attribute: `channel`, displayName: `Channel`, type: FilterValueType.string },
]

const getDefaultColumns = (showSensitiveColumns: boolean, locationId: string): ColumnDef<BudgetRow>[] => {
  return compact([
    {
      accessorFn: (row: BudgetRow) => row.yearMonth,
      id: `Date`,
      cell: (info) => (
        <Link to={`${locationId}-${info.row.original.yearMonth}-${snakeCase(info.row.original.channel)}`}>
          <span className="font-semibold text-strong hover:underline">
            {moment.utc(info.getValue() as string).format(`MMM YYYY`)}
          </span>
        </Link>
      ),
      header: `Date`,
      meta: { sortKey: `yearMonth`, defaultVisible: true, pinned: true },
      enableHiding: false,
    },
    {
      accessorFn: (row: BudgetRow) => `$${row.amount?.toLocaleString(`en-US`) ?? 0}`,
      id: `Budget Amount`,
      cell: (info) => info.getValue(),
      header: `Budget Amount`,
      meta: { sortKey: `amount`, defaultVisible: true },
    },
    showSensitiveColumns
      ? {
          accessorFn: (row: BudgetRow) => `$${row.targetSpend?.toLocaleString(`en-US`) ?? 0}`,
          id: `Target Spend`,
          cell: (info) => info.getValue(),
          header: `Target Spend`,
          meta: { sortKey: `targetSpend`, defaultVisible: true },
        }
      : null,
    {
      accessorFn: (row: BudgetRow) => `${(row.leadGoal || 0)?.toLocaleString(`en-US`)}`,
      id: `Lead Goal`,
      cell: (info) => info.getValue(),
      header: `Lead Goal`,
      meta: { sortKey: `leadGoal`, defaultVisible: true },
    },
    {
      accessorFn: (row: BudgetRow) => (row.leadsGenerated || 0).toLocaleString(`en-US`),
      id: `Leads Gen.`,
      cell: (info) => info.getValue(),
      header: `Leads Gen.`,
      meta: { sortKey: `leadsGenerated`, defaultVisible: true },
    },
    {
      accessorFn: (row: BudgetRow) => `${(row.patientGoal || 0)?.toLocaleString(`en-US`)}`,
      id: `Patient Goal`,
      cell: (info) => info.getValue(),
      header: `Patient Goal`,
      meta: { sortKey: `patientGoal`, defaultVisible: true },
    },
    {
      accessorFn: (row: BudgetRow) => (row.patientsGenerated || 0).toLocaleString(`en-US`),
      id: `Patients Gen.`,
      cell: (info) => info.getValue(),
      header: `Patients Gen.`,
      meta: { sortKey: `patientsGenerated`, defaultVisible: true },
    },
    {
      accessorFn: (row: BudgetRow) => row.channel,
      id: `Channel`,
      cell: (info) => info.getValue(),
      header: `Channel`,
      meta: { sortKey: `channel.keyword`, defaultVisible: true },
    },
  ])
}

export function useBudgetList(): UseBudgetListReturn {
  const { locationId = `` } = useParams()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const checkPermission = useCheckPermission()
  const navigate = useNavigate()
  const store = useBudgetStore()

  useEffect(() => {
    store.fetchList({ search: `locationId:${locationId}` })
  }, [locationId])

  const showSpend = staffV2Enabled ? checkPermission([`org:marketing:see-spend`, `marketing:spend`]) : false

  const filteredRows = useClientSideTableFilters(
    (store.hits as unknown as BudgetRow[]) || [],
    filterableColumns,
    `id`,
    [`channel`],
  )

  return {
    filteredRows: filteredRows as BudgetRow[],
    columns: getDefaultColumns(showSpend, locationId),
    onAddNew: () => navigate(`new`),
  }
}
